/**
 * Team and roles: the dashboard's fourth screen.
 *
 * The members, one invitation form, and what each role can do. On a live
 * tenant (Sep 10 2026) the screen is the Worker's four member routes (GET,
 * POST, PATCH and DELETE on /api/enterprise/members, in routes/session.ts)
 * with the rules the screen mirrors: an invitation goes only to a domain an
 * active member already holds, a role change or a removal revokes that
 * person's sessions, and only an owner can make another owner. The Worker's
 * refusals (422 for a foreign domain, 409 for a duplicate or the last owner)
 * are shown in its own words. /demo renders the sample team and its
 * controls change the view only.
 *
 * No explainer copy. The roles card IS the explanation, three lines, and the
 * rest of the screen is rows and controls.
 */

import { useMemo, useState } from "react";

import {
  AdminShell,
  ExportPdfButton,
  type PaletteCommand,
} from "~/app/components/AdminShell";
import { LiveGate, useLiveTenant } from "~/app/components/LiveTenant";
import { viewerOf, type SessionPayload } from "~/app/lib/session";
import {
  Button,
  Card,
  CardBody,
  CardFootnote,
  CardHeader,
  CardTitle,
  Chip,
  DataTable,
  Field,
  Input,
  SectionHead,
  Select,
  StateCard,
} from "~/app/components/primitives";
import { ADMIN_SECTIONS } from "~/app/lib/admin-nav";
import { changeMemberRole, inviteMember, removeMember, type MemberRole } from "~/app/lib/api";
import {
  DEFAULT_FILTERS,
  deriveCohort,
  inviteError,
  sampleTeam,
  scenarioFromSearch,
  type TeamMember,
  type TeamRole,
} from "~/app/lib/demo-data";
import { formatDate } from "~/app/lib/format";
import { liveTeam, liveView } from "~/app/lib/live-view";
import { DefinitionRows } from "~/app/panels/panel-charts";

const ROLES: readonly TeamRole[] = ["Viewer", "Admin", "Owner"];

/** Owner: everything. Admin: everyone but owners. Viewer: nobody. */
function canAssign(actor: TeamRole, role: TeamRole): boolean {
  if (actor === "Owner") return true;
  if (actor === "Admin") return role !== "Owner";
  return false;
}

/** The screen's role names to the Worker's. */
function toWire(role: TeamRole): MemberRole {
  return role === "Owner" ? "owner" : role === "Admin" ? "admin" : "viewer";
}

export function AdminTeam({
  basePath,
  demo = false,
  session,
}: {
  basePath: "/admin" | "/demo";
  demo?: boolean;
  /** The signed-in person, on /admin. Their team replaces the sample's. */
  session?: SessionPayload;
}) {
  const live = useLiveTenant(session);
  const scenario = useMemo(
    () => scenarioFromSearch(typeof window === "undefined" ? "" : window.location.search),
    [],
  );
  const sampleView = useMemo(() => deriveCohort(scenario, DEFAULT_FILTERS), [scenario]);
  const data = live.state.kind === "ready" ? live.state.data : null;
  const view = useMemo(() => (data ? liveView(data) : sampleView), [data, sampleView]);

  const [sampleMembers, setSampleMembers] = useState<readonly TeamMember[]>(() =>
    sampleTeam(sampleView.institution),
  );
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TeamRole>("Viewer");
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (session && live.state.kind !== "ready") {
    return <LiveGate state={live.state} reload={live.reload} />;
  }

  const { institution, seat } = view;
  const me = institution.viewerRole;
  const isLive = data !== null && session !== undefined;
  const team: readonly TeamMember[] =
    isLive ? (data.members ? liveTeam(data.members, session) : []) : sampleMembers;
  const membersDown = isLive && data.members === null;

  const activeOwners = team.filter((m) => m.role === "Owner" && m.status === "Active").length;

  const invite = async () => {
    if (!isLive) {
      const reason = inviteError(email, team);
      if (reason) {
        setError(reason);
        return;
      }
      setSampleMembers([
        ...team,
        {
          id: `mem-${Date.now()}`,
          name: null,
          email: email.trim().toLowerCase(),
          role,
          status: "Invited",
          lastSeen: null,
        },
      ]);
      setEmail("");
      setError(null);
      // The sample banner used to carry this; it left every screen on Sep 17
      // 2026 (Thomas), so the one place it is load-bearing says it instead:
      // a row appearing marked Invited is the only thing here that could be
      // read as an email having gone out.
      setNotice("Added to the sample list. Nothing is sent from the sample.");
      return;
    }
    const address = email.trim();
    if (address === "") {
      setError("Enter a work email address.");
      return;
    }
    setBusy(true);
    const result = await inviteMember(address, toWire(role));
    setBusy(false);
    if (result.ok) {
      setEmail("");
      setError(null);
      setNotice(`${address} is on your team as invited. They sign in with that address at this dashboard's Login.`);
      live.reload();
    } else {
      // 400, 403, 422 and 409 arrive as the Worker's sentences.
      setError(result.error.message);
    }
  };

  const changeRole = async (id: string, next: TeamRole) => {
    if (!isLive) {
      setSampleMembers(team.map((m) => (m.id === id ? { ...m, role: next } : m)));
      return;
    }
    setBusy(true);
    const result = await changeMemberRole(id, toWire(next));
    setBusy(false);
    if (result.ok) {
      setNotice(null);
      live.reload();
    } else {
      setNotice(result.error.message);
    }
  };

  const remove = async (id: string) => {
    if (confirming !== id) {
      setConfirming(id);
      window.setTimeout(() => setConfirming((c) => (c === id ? null : c)), 4000);
      return;
    }
    setConfirming(null);
    if (!isLive) {
      setSampleMembers(team.filter((m) => m.id !== id));
      return;
    }
    setBusy(true);
    const result = await removeMember(id);
    setBusy(false);
    if (result.ok) {
      setNotice(null);
      live.reload();
    } else {
      setNotice(result.error.message);
    }
  };

  const commands: PaletteCommand[] = [
    {
      id: "print",
      group: "Share",
      label: "Export as PDF, or print",
      keywords: "board pdf paper export print",
      run: () => window.print(),
    },
  ];

  return (
    <AdminShell
      institution={institution}
      viewer={viewerOf(session)}
      basePath={basePath}
      page="team"
      isSample={!isLive}
      sections={[...ADMIN_SECTIONS]}
      seat={seat}
      headerActions={<ExportPdfButton />}
      commands={commands}
    >
      <section className="e-sec" style={{ marginTop: 0 }}>
        <SectionHead
          level="page"
          id="team"
          title="Team and roles"
          lede="Who can open this dashboard, and what each role can do."
        />


        <div className="e-grid">
          <Card variant="table" span={me === "Viewer" ? "full" : 2}>
            <CardHeader>
              <CardTitle>Members</CardTitle>
            </CardHeader>
            <CardBody>
              {membersDown ? (
                <StateCard
                  variant="down"
                  title="We could not load your team"
                  action={
                    <Button variant="secondary" size="sm" onClick={live.reload}>
                      Try again
                    </Button>
                  }
                >
                  The rest of the dashboard is unaffected.
                </StateCard>
              ) : (
                <DataTable
                  caption="Members of this institution's dashboard"
                  rows={team}
                  rowKey={(m) => m.id}
                  cap={50}
                  moreNoun="member"
                  stackTitle={(m) => m.name ?? m.email}
                  columns={[
                    {
                      key: "person",
                      header: "Person",
                      // The email sits under the name, the way the member app's
                      // footer stacks a name over a handle: six columns did not
                      // fit the card, and the address is the row's identity.
                      cell: (m) => (
                        <span className="e-team-person">
                          <span className="e-ui">
                            {m.name ?? m.email}
                            {m.you ? <span className="e-note"> (you)</span> : null}
                          </span>
                          {m.name ? <span className="e-note">{m.email}</span> : null}
                        </span>
                      ),
                      text: (m) => (m.name ? `${m.name} ${m.email}` : m.email),
                    },
                    {
                      key: "role",
                      header: "Role",
                      cell: (m) =>
                        m.you || !canAssign(me, m.role) || (m.role === "Owner" && activeOwners === 1) ? (
                          <span className="e-ui">{m.role}</span>
                        ) : (
                          <Select
                            className="e-team-role"
                            aria-label={`Role for ${m.name ?? m.email}`}
                            value={m.role}
                            disabled={busy}
                            onChange={(event) => void changeRole(m.id, event.target.value as TeamRole)}
                          >
                            {ROLES.filter((r) => canAssign(me, r)).map((r) => (
                              <option key={r} value={r}>
                                {r}
                              </option>
                            ))}
                          </Select>
                        ),
                      text: (m) => m.role,
                      align: "left",
                    },
                    {
                      key: "status",
                      header: "Status",
                      cell: (m) => (m.status === "Invited" ? <Chip tone="warn">Invited</Chip> : "Active"),
                      text: (m) => m.status,
                      align: "left",
                    },
                    {
                      key: "seen",
                      header: "Last seen",
                      cell: (m) => (
                        <span style={{ whiteSpace: "nowrap" }}>{m.lastSeen ? formatDate(m.lastSeen) : "Not yet"}</span>
                      ),
                      text: (m) => (m.lastSeen ? formatDate(m.lastSeen) : "Not yet"),
                      secondary: true,
                    },
                    {
                      key: "actions",
                      header: "",
                      cell: (m) =>
                        m.you || !canAssign(me, m.role) ? null : (
                          <Button
                            variant={confirming === m.id ? "danger" : "ghost"}
                            size="sm"
                            disabled={busy}
                            onClick={() => void remove(m.id)}
                          >
                            {confirming === m.id ? "Confirm removal" : "Remove"}
                          </Button>
                        ),
                      text: () => "",
                      align: "right",
                    },
                  ]}
                />
              )}
              {notice ? (
                <p className="e-note" role="status" style={{ marginTop: "var(--sp-3)" }}>
                  {notice}
                </p>
              ) : null}
            </CardBody>
            <CardFootnote>
              <p className="e-note">Removing someone ends their sessions at once.</p>
            </CardFootnote>
          </Card>

          {/* A viewer cannot invite (the Worker's route is for administrators
              and owners), so the card is not offered to one. */}
          {me === "Viewer" ? null : (
            <Card solo>
              <CardHeader>
                <CardTitle>Invite a colleague</CardTitle>
              </CardHeader>
              <CardBody>
                <form
                  className="e-vstack"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void invite();
                  }}
                >
                  <Field label="Work email" error={error ?? undefined}>
                    <Input
                      type="email"
                      autoComplete="off"
                      value={email}
                      placeholder={`name@${institution.teamDomain}`}
                      aria-invalid={error ? "true" : undefined}
                      onChange={(event) => {
                        setEmail(event.target.value);
                        if (error) setError(null);
                      }}
                    />
                  </Field>
                  <Field label="Role">
                    <Select value={role} onChange={(event) => setRole(event.target.value as TeamRole)}>
                      {ROLES.filter((r) => canAssign(me, r)).map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <div>
                    <Button type="submit" variant="primary" size="sm" disabled={busy}>
                      Send invitation
                    </Button>
                  </div>
                </form>
              </CardBody>
              <CardFootnote>
                <p className="e-note">
                  Only addresses at a domain your team already uses here. Invitations expire after 14 days.
                </p>
              </CardFootnote>
            </Card>
          )}
        </div>

        <div className="e-grid">
          <Card span="full">
            <CardHeader>
              <CardTitle>What each role can do</CardTitle>
            </CardHeader>
            <CardBody>
              <DefinitionRows
                rows={[
                  { label: "Owner", value: <span className="e-ui">Billing, members and data.</span> },
                  { label: "Admin", value: <span className="e-ui">Members and data.</span> },
                  { label: "Viewer", value: <span className="e-ui">Data only. The capacity band is hidden.</span> },
                ]}
              />
            </CardBody>
          </Card>
        </div>
      </section>
    </AdminShell>
  );
}
